import {
  Box,
  Chip,
  IconButton,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import type { ChangeEvent, ReactNode } from "react";
import type { SystemStyleObject } from "@mui/system";
import {
  ACCENT_COLOR,
  FIELD_BORDER,
  FIELD_RADIUS,
  YES_NO_LABEL_WIDTH,
  YES_NO_SWITCH_HEIGHT,
  YES_NO_SWITCH_PADDING,
  YES_NO_SWITCH_TRANSLATE,
  YES_NO_SWITCH_WIDTH,
  chipSx,
  fieldTitleSx,
  inputSx,
} from "../../styles";

const YesNoSwitch = styled(Switch)(() => ({
  width: YES_NO_SWITCH_WIDTH,
  height: YES_NO_SWITCH_HEIGHT,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: YES_NO_SWITCH_PADDING,
    transitionDuration: "200ms",
    "&.Mui-checked": {
      transform: `translateX(${YES_NO_SWITCH_TRANSLATE}px)`,
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: ACCENT_COLOR,
        opacity: 1,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    width: 20,
    height: 20,
    backgroundColor: "#fff",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.2)",
  },
  "& .MuiSwitch-track": {
    borderRadius: YES_NO_SWITCH_HEIGHT / 2,
    backgroundColor: "#E5E7EB",
    opacity: 1,
    position: "relative",
    "&::before, &::after": {
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      fontSize: "12px",
      fontWeight: 600,
      width: YES_NO_LABEL_WIDTH,
      textAlign: "center",
      pointerEvents: "none",
    },
    "&::before": {
      content: '"Yes"',
      left: YES_NO_SWITCH_PADDING,
      opacity: 0,
      color: "#fff",
    },
    "&::after": {
      content: '"No"',
      right: YES_NO_SWITCH_PADDING,
      opacity: 1,
      color: "#6B7280",
    },
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track::before": {
    opacity: 1,
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track::after": {
    opacity: 0,
  },
}));

export const FieldBlock = ({
  title,
  titleSx,
  children,
}: {
  title: string;
  titleSx?: SystemStyleObject;
  children: ReactNode;
}) => (
  <Box>
    <Typography
      variant="body2"
      sx={{ ...fieldTitleSx, ...(titleSx ?? {}) }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

export const ChipsField = ({
  title,
  titleSx,
  values,
}: {
  title: string;
  titleSx?: SystemStyleObject;
  values: string[];
}) => (
  <Box>
    <Typography
      variant="body2"
      sx={{ ...fieldTitleSx, ...(titleSx ?? {}) }}
    >
      {title}
    </Typography>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
      {values.map((value) => (
        <Chip
          key={`${title}-${value}`}
          label={value}
          size="small"
          variant="outlined"
          sx={chipSx}
        />
      ))}
    </Box>
  </Box>
);

export const ReadOnlyField = ({ label, value }: { label: string; value: string }) => (
  <TextField
    label={label}
    value={value}
    size="small"
    InputLabelProps={{ shrink: true }}
    fullWidth
    InputProps={{ readOnly: true }}
    sx={inputSx}
  />
);

export const ReadOnlyChips = ({
  label,
  titleSx,
  values,
}: {
  label: string;
  titleSx?: SystemStyleObject;
  values: string[];
}) => (
  <Box>
    <Typography
      variant="body2"
      sx={{ ...fieldTitleSx, ...(titleSx ?? {}) }}
    >
      {label}
    </Typography>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
      {values.map((value) => (
        <Chip
          key={`${label}-${value}`}
          label={value}
          size="small"
          variant="outlined"
          sx={chipSx}
        />
      ))}
    </Box>
  </Box>
);

export const StepperField = ({
  value,
  onDecrease,
  onIncrease,
}: {
  value: number;
  onDecrease: () => void;
  onIncrease: () => void;
}) => (
  <Box
    sx={{
      border: `1px solid ${FIELD_BORDER}`,
      borderRadius: "12px",
      display: "grid",
      gridTemplateColumns: "48px 1fr 48px",
      height: 48,
      overflow: "hidden",
      backgroundColor: "#fff",
    }}
  >
    <IconButton
      onClick={onDecrease}
      sx={{
        borderRadius: 0,
        height: "100%",
        width: "100%",
        backgroundColor: "#F3F4F6",
        borderRight: `1px solid ${FIELD_BORDER}`,
      }}
    >
      <RemoveIcon fontSize="small" />
    </IconButton>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
        color: "#111827",
      }}
    >
      {value}
    </Box>
    <IconButton
      onClick={onIncrease}
      sx={{
        borderRadius: 0,
        height: "100%",
        width: "100%",
        backgroundColor: "#F3F4F6",
        borderLeft: `1px solid ${FIELD_BORDER}`,
      }}
    >
      <AddIcon fontSize="small" />
    </IconButton>
  </Box>
);

export const SectionHeader = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) => (
  <Box>
    <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: "16px" }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

export const YesNoToggle = ({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (value: boolean) => void;
}) => (
  <YesNoSwitch
    checked={value}
    onChange={(_event: ChangeEvent<HTMLInputElement>, checked: boolean) =>
      onChange(checked)
    }
    inputProps={{ "aria-label": value ? "Yes" : "No" }}
  />
);
